import {
  CheckCircleFilled,
  EditFilled,
  ExclamationCircleOutlined,
  LoadingOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { Alert, Col, Row, Skeleton, message } from 'antd';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';

import { Button, CheckList, Collapse, Input } from '../..';
import {
  useConfirmFinancialTableMutation,
  useRemoveFinancialTableConfirmationMutation,
} from '../../../services/financialTableApi';
import {
  useGetConsentSignalQuery,
  useLazyGetConsentSignalQuery,
} from '../../../services/pspApi';
import {
  useGetStopoverDockingChecklistQuery,
  usePatchStopoverMutation,
} from '../../../services/stopoverApi';
import {
  ConsentSignalGridType,
  FinancialTableType,
  Stopover,
} from '../../../types';
import {
  createDateStringPtBr,
  removeSpecialCharacters,
} from '../../../utils/utils';
import { Modal } from '../../antd/Modal';
import {
  ConsentSignalLegend,
  ConsentSignalTable,
} from '../consentSignal/ConsentSignalTable';
import { EmptyDataPlaceholder } from '../emptyDataPlaceholder/EmptyDataPlaceholder';
import { panelHeaderTitle } from '../utils';

const { Panel } = Collapse;
const { confirm } = Modal;

type DocumentsConsentsDescriptionProps = {
  activeKey: string | string[];
  onOpenPanel: (panel: string | string[]) => void;
  selectedStopover: Stopover;
  setSelectedStopover?: (stopover: Stopover) => void;
};

const { TextArea } = Input;

// Anuências v2 (PMIS-475): sinaleiro no padrão do PSP. A coluna "Dados", a
// confirmação do responsável financeiro e as observações são as do v1
// (documentsConsentsData/), que fica no código para eventual volta.
export function DocumentsConsentsDescriptionV2(
  props: DocumentsConsentsDescriptionProps
) {
  const { activeKey, onOpenPanel, selectedStopover, setSelectedStopover } =
    props;

  const [Data, setData] = useState<
    {
      label: string;
      checked?: boolean;
      subItens?: {
        title: string;
        checked: boolean | null;
        extra?: React.ReactNode;
      }[];
    }[]
  >([]);

  const duv = removeSpecialCharacters(selectedStopover.duv || '') || '';
  const {
    data: storedGrid,
    isLoading: isLoadingConsents,
    error: consentsError,
  } = useGetConsentSignalQuery({ duv }, { skip: !duv });
  const [refreshConsents, { isFetching: isRefreshingConsents }] =
    useLazyGetConsentSignalQuery();
  // Resultado do "Atualizar"; some ao trocar de escala.
  const [refreshedGrid, setRefreshedGrid] = useState<ConsentSignalGridType>();
  useEffect(() => setRefreshedGrid(undefined), [duv]);
  const consentGrid = refreshedGrid || storedGrid;

  async function handleRefreshConsents() {
    const response = await refreshConsents({ duv, refresh: true });
    if (response.data) {
      setRefreshedGrid(response.data);
      if (response.data.refresh_throttled) {
        message.info(
          'O PSP foi consultado há menos de um minuto; exibindo a última atualização.'
        );
      } else if (response.data.refresh_error) {
        message.warning(
          'Não foi possível atualizar com o PSP; exibindo a última atualização.'
        );
      } else {
        message.success('Anuências atualizadas com o PSP');
      }
    }
  }

  const { data: checklist, isLoading } = useGetStopoverDockingChecklistQuery(
    selectedStopover?.id || null
  );

  const [observations, setObservations] = useState(
    selectedStopover.observations || ''
  );
  const [isEditObs, setIsEditObs] = useState(false);

  useEffect(() => {
    setObservations(selectedStopover.observations || '');
  }, [selectedStopover.observations]);

  const [confirmFinancialTable, { isLoading: isLoadingConfirm }] =
    useConfirmFinancialTableMutation();
  const [
    removeFinancialTableConfirmation,
    { isLoading: isLoadingRemoveConfirmation },
  ] = useRemoveFinancialTableConfirmationMutation();

  function updateFinancialTable(
    id: number,
    changes: Partial<FinancialTableType>
  ) {
    if (!setSelectedStopover) return;
    setSelectedStopover({
      ...selectedStopover,
      dockings: selectedStopover.dockings?.map((docking) => ({
        ...docking,
        financial_tables: docking.financial_tables?.map((financialTable) =>
          financialTable.id === id
            ? { ...financialTable, ...changes }
            : financialTable
        ),
      })),
    });
  }

  function showConfirmFinancialTable(financialTable: FinancialTableType) {
    confirm({
      title: 'Confirmar responsável financeiro',
      icon: <ExclamationCircleOutlined />,
      content: `Deseja confirmar ${financialTable.company.name} como responsável financeiro?`,
      okText: 'Sim, confirmar',
      cancelText: 'Voltar',
      async onOk() {
        const response = await confirmFinancialTable(financialTable.id);
        if ('data' in response) {
          updateFinancialTable(financialTable.id, {
            company_confirmed: true,
            rejection_reason: '',
            manually_confirmed_by: response.data?.manually_confirmed_by,
            manually_confirmed_at: response.data?.manually_confirmed_at,
          });
          message.success('Responsável financeiro confirmado');
        }
      },
    });
  }

  function showRemoveFinancialTableConfirmation(
    financialTable: FinancialTableType
  ) {
    confirm({
      title: 'Remover confirmação',
      icon: <ExclamationCircleOutlined />,
      content: `Deseja remover a confirmação de ${financialTable.company.name}?`,
      okText: 'Sim, remover',
      okType: 'danger',
      cancelText: 'Voltar',
      async onOk() {
        const response = await removeFinancialTableConfirmation(
          financialTable.id
        );
        if (!('error' in response)) {
          updateFinancialTable(financialTable.id, {
            company_confirmed: null,
            manually_confirmed_by: null,
            manually_confirmed_at: null,
          });
          message.success('Confirmação removida');
        }
      },
    });
  }

  function getManualConfirmationText(financialTable: FinancialTableType) {
    const user = financialTable.manually_confirmed_by;
    if (!user) return null;
    const userName =
      `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
      user.username;
    const confirmedAt = createDateStringPtBr(
      financialTable.manually_confirmed_at
    );
    return `Confirmado por ${userName}${
      confirmedAt ? ` em ${confirmedAt}` : ''
    }`;
  }

  function getFinancialTableExtra(financialTable: FinancialTableType) {
    const manualConfirmationText = getManualConfirmationText(financialTable);
    const isLoadingAction = isLoadingConfirm || isLoadingRemoveConfirmation;
    return (
      <>
        {manualConfirmationText && (
          <div
            style={{
              color: 'var(--neutral_medium)',
              fontSize: '12px',
              paddingLeft: '24px',
            }}
          >
            {manualConfirmationText}
          </div>
        )}
        {setSelectedStopover &&
          financialTable.company &&
          (financialTable.company_confirmed !== true ? (
            <Button
              type="link"
              size="small"
              disabled={isLoadingAction}
              onClick={() => showConfirmFinancialTable(financialTable)}
              style={{ paddingLeft: '24px' }}
            >
              Confirmar
            </Button>
          ) : (
            financialTable.manually_confirmed_by && (
              <Button
                type="link"
                size="small"
                danger
                disabled={isLoadingAction}
                onClick={() =>
                  showRemoveFinancialTableConfirmation(financialTable)
                }
                style={{ paddingLeft: '24px' }}
              >
                Remover confirmação
              </Button>
            )
          ))}
      </>
    );
  }

  function getCurrentDockingFinancialTables() {
    const currentDocking = selectedStopover.dockings?.find(
      (docking) => docking.id === selectedStopover.current_docking_id
    );

    return isEmpty(currentDocking?.financial_tables) ||
      !currentDocking?.financial_tables
      ? undefined
      : currentDocking?.financial_tables.map((financialTable) => {
          return {
            title: financialTable.company.name,
            name: `financial-table-${financialTable.id}`,
            checked: financialTable.company_confirmed,
            extra: getFinancialTableExtra(financialTable),
          };
        });
  }

  // "Deslocamento para entrada" fica fora da v2 (PMIS-475): gerava alerta falso.
  useEffect(() => {
    setData([
      {
        label: 'Calado de entrada',
        checked: checklist?.input_draught || false,
      },
      {
        label: 'Confirmação de armazenamento',
        checked: checklist?.storage_confirmed || false,
      },
      {
        label: 'DUV - Documento Único Virtual',
        checked: checklist?.duv || false,
      },
      {
        label: 'Comprimento (LOA)',
        checked: checklist?.length || false,
      },
      {
        label: 'DWT de entrada',
        checked: checklist?.input_dwt || false,
      },
      {
        label: 'Tipo de cabo',
        checked: checklist?.dock_line_type || false,
      },
      {
        label: 'Confirmação dos responsáveis financeiros',
        checked: checklist?.financial_tables_confirmed || false,
        subItens: getCurrentDockingFinancialTables(),
      },
      {
        label: 'Manifesto de carga',
        checked: checklist?.cargo_manifest_data || false,
      },
    ]);
  }, [
    checklist,
    selectedStopover.dockings,
    isLoadingConfirm,
    isLoadingRemoveConfirmation,
  ]);

  const [patchStopover, { isLoading: isLoadingPatchStopover }] =
    usePatchStopoverMutation();
  async function handleSubmitObs() {
    await patchStopover({ id: selectedStopover.id, observations });
    setIsEditObs(false);
  }

  return (
    <Collapse
      activeKey={activeKey}
      onChange={onOpenPanel}
      expandIconPosition="end"
    >
      <Panel
        forceRender
        header="Documentações e anuências"
        key="documentsConsents"
        extra={panelHeaderTitle(selectedStopover.updated_at)}
      >
        <div style={{ marginTop: '10px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '8px',
            }}
          >
            <span>Sinaleiro de anuências (PSP):</span>
            {duv && (
              <span
                style={{ fontSize: '12px', color: 'var(--neutral_medium)' }}
              >
                {consentGrid?.synced_at &&
                  `Atualizado em ${createDateStringPtBr(
                    consentGrid.synced_at
                  )} `}
                <Button
                  type="link"
                  size="small"
                  icon={<SyncOutlined spin={isRefreshingConsents} />}
                  disabled={isRefreshingConsents}
                  onClick={handleRefreshConsents}
                >
                  Atualizar
                </Button>
              </span>
            )}
          </div>
          <Skeleton loading={isLoadingConsents}>
            {!duv ? (
              <EmptyDataPlaceholder text="Preencha a DUV para exibir o status das anuências" />
            ) : consentGrid ? (
              <>
                {consentGrid.sync_error &&
                  consentGrid.source === 'snapshot' && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: '8px' }}
                      message="A última tentativa de atualizar com o PSP falhou; exibindo a última atualização bem-sucedida."
                    />
                  )}
                <ConsentSignalTable grid={consentGrid} />
                <ConsentSignalLegend />
              </>
            ) : (
              <EmptyDataPlaceholder
                text={
                  consentsError ? (
                    'Não foi possível consultar o PSP para esta DUV. Tente atualizar.'
                  ) : (
                    <>
                      Não há dados no PSP para a DUV informada:{' '}
                      <span
                        style={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                      >
                        {selectedStopover.duv}
                      </span>
                    </>
                  )
                }
              />
            )}
          </Skeleton>
        </div>
        <Row style={{ marginTop: '21px' }}>
          <Col span={11}>
            <Skeleton loading={isLoading}>
              <CheckList
                title="Dados:"
                items={Data}
                labelField="label"
                isCollapsable
              />
            </Skeleton>
          </Col>
        </Row>
        <div
          style={{
            display: 'flex',
            gap: '5px',
            marginBlockStart: '21px',
          }}
        >
          <span
            style={{
              color: 'var(--neutral_medium)',
              fontSize: '12px',
              fontWeight: 'bold',
            }}
          >
            Observações:
          </span>
          <TextArea
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
            disabled={!isEditObs || isLoadingPatchStopover}
            onPressEnter={handleSubmitObs}
            maxLength={2000}
            placeholder="Digite aqui as observações"
            bordered
            style={{
              minHeight: 80,
              fontSize: 12,
              color: '#000000d9',
              fontWeight: 500,
            }}
          />
          <Button
            icon={
              isLoadingPatchStopover ? (
                <LoadingOutlined />
              ) : isEditObs ? (
                <CheckCircleFilled />
              ) : (
                <EditFilled />
              )
            }
            onClick={() =>
              isEditObs
                ? handleSubmitObs()
                : setIsEditObs((editObs) => !editObs)
            }
            type="link"
            style={{ marginTop: '-8px' }}
          />
        </div>
      </Panel>
    </Collapse>
  );
}
